import React, { useEffect, useState, useCallback, memo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Chip,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  InputLabel,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  ShoppingCartOutlined,
  AssignmentTurnedInOutlined,
  LocalShippingOutlined,
  CheckCircleOutline,
  CurrencyRupee,
  Cancel,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { BarChart, PieChart } from "@mui/x-charts";
import CountUp from "react-countup";
import { toast } from "react-toastify";
import {
  getDashboardStats,
  getDashboardBranch,
} from "../service/dashboardService";
import { getAllBranchLists } from "../service/branchService";
import Color from "../components/Color";

const MemoizedCard = memo(({ stat }) => (
  <Box
    display="flex"
    flex="1 1 auto"
    width={{ xs: "47%", sm: "45%", md: "24%", lg: "22.7%" }}
  >
    <Card
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: { xs: 80, sm: 90, md: 100, lg: 110 },
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: "none",
        background: stat.gradient,
        color: "white",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 }, flex: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: {
                  xs: "0.75rem",
                  sm: "0.9rem",
                  md: "1rem",
                  lg: "1.1rem",
                },
                mt: { xs: 0.5, sm: 0.8, md: 1 },
                lineHeight: 1.2,
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                fontSize: {
                  xs: "0.9rem",
                  sm: "1.1rem",
                  md: "1.3rem",
                  lg: "1.5rem",
                },
                mt: { xs: 0.5, sm: 0.8, md: 1 },
              }}
            >
              {stat.prefix || ""}
              <CountUp
                end={parseFloat(stat.value) || 0}
                duration={1.5}
                decimals={stat.prefix ? 2 : 0}
              />
            </Typography>
          </Box>
          <Avatar
            sx={{
              bgcolor: "white",
              width: { xs: 28, sm: 40, md: 50, lg: 60, xl: 70 },
              height: { xs: 28, sm: 40, md: 50, lg: 60, xl: 70 },
            }}
          >
            {stat.icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  </Box>
));

const MemoizedBarChart = memo(({ monthly_sales }) => (
  <Box
    display="flex"
    width={{ xs: "100%", sm: "48.5%", md: "48.65%", lg: "49.2%" }}
  >
    <Card
      sx={{
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: "none",
        height: { xs: 300, sm: 330, md: 400, lg: 450, xl: 480 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        mt: { xs: 1, sm: 2 },
        outline: "none",
        "&:focus": { outline: "none" },
        "& *:focus": { outline: "none !important" },
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
        <Typography
          variant="h6"
          gutterBottom
          fontWeight="bold"
          mb={{ xs: 1, sm: 2, md: 3 }}
          sx={{
            fontSize: {
              xs: "0.8rem",
              sm: "0.9rem",
              md: "1.1rem",
              lg: "1.25rem",
              xl: "1.3rem",
            },
          }}
        >
          Monthly Sales
        </Typography>
        <BarChart
          xAxis={[
            {
              scaleType: "band",
              data: monthly_sales.map((item) => item.month),
              tickLabelStyle: {
                fontSize: { xs: 10, sm: 11, md: 12, lg: 14 },
                
              },
            },
          ]}
          yAxis={[
            {
              valueFormatter: (value) => `₹${value.toFixed(0)}`,
              tickLabelStyle: {
                fontSize: { xs: 10, sm: 11, md: 12, lg: 14 },
              },
            },
          ]}
          series={[
            {
              data: monthly_sales.map((item) => item.total),
              color: "#d24419ff",
              valueFormatter: (value) => `₹${(Number(value) || 0).toFixed(2)}`,
            },
          ]}
          height={320}
          width={450}
          slotProps={{
            bar: {
              rx: 6,
              ry: 6,
            },
          }}
          margin={{ top: 10, bottom: 40, left: 50, right: 20 }}
        />
      </CardContent>
    </Card>
  </Box>
));

const MemoizedPieChart = memo(({ top_selling_products }) => {
  const COLORS = ["#4B7BEC", "#FF6B6B", "#1DD1A1", "#ff23c8ff", "#FF8C00"];
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // mobile
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // tablet

  // Responsive chart height
  // const chartHeight =  isSm ? 260 : 300;
  // const chartWidth =  isSm ? 450 : 500;

  return (
    <Box
      display="flex"
      width={{ xs: "100%", sm: "48.5%", md: "48.65%", lg: "49.2%" }}
    >
      <Card
        sx={{
          borderRadius: { xs: 1, sm: 2 },
          boxShadow: "none",
          height: { xs: 300, sm: 330, md: 400, lg: 450, xl: 480 },
          width: "100%",
          maxWidth: "100%",
          mt: { xs: 1, sm: 2 },
          outline: "none",
        }}
      >
        <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={{ xs: 1, sm: 2 }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                fontSize: {
                  xs: "0.8rem",
                  sm: "0.9rem",
                  md: "1.1rem",
                  lg: "1.25rem",
                },
              }}
            >
              Top Selling Products
            </Typography>
          </Box>

          {top_selling_products.length > 0 ? (
            <PieChart
              series={[
                {
                  data: top_selling_products.slice(0, 5).map((product, index) => ({
                    id: index,
                    value: Number(product.total_revenue) || 0,
                    label: product.name || `Product ${index + 1}`,
                    color: COLORS[index % COLORS.length],
                  })),
                  innerRadius: 40,
                  outerRadius: 100,
                  paddingAngle: 5,
                  valueFormatter: (item) =>
                    `₹${(Number(item.value) || 0).toFixed(2)}`,
                  arcLabel: () => "",
                },
              ]}
              height={300}
              width={500}
              slotProps={{
                legend: {
                  direction: isXs ? "row" : "column", // mobile = row, others = column
                  position: {
                    vertical: isXs ? "bottom" : "bottom",
                    horizontal: isXs ? "bottom" : "right",
                  },
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: {
                    fontSize: isXs
                      ? "0.5rem"
                      : isSm
                      ? "0.5rem"
                      : "0.6rem",
                  },
                  padding: 10,
                },
              }}
            />
          ) : (
            <Typography
              color="textSecondary"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.9rem", md: "1rem" } }}
            >
              No products found
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
});


const MemoizedRecentOrders = memo(({ recent_orders }) => (
  <Grid item xs={12} mt={{ xs: 2, sm: 3, md: 4 }} width="100%">
    <Card
      sx={{
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: "none",
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        mb: { xs: 1, sm: 2 },
        boxSizing: "border-box",
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
        <Typography
          variant="h6"
          gutterBottom
          fontWeight="bold"
          sx={{
            fontSize: {
              xs: "0.8rem",
              sm: "0.9rem",
              md: "1.1rem",
              lg: "1.25rem",
            },
          }}
        >
          Recent Orders
        </Typography>
        <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
        <Table
          sx={{
            minWidth: { xs: 280, sm: 400, md: 600, lg: 750 },
            width: "100%",
            maxWidth: "100%",
            "& .MuiTableCell-root": {
              padding: { xs: "6px 4px", sm: "8px", md: "12px", lg: "16px" },
              fontSize: {
                xs: "0.65rem",
                sm: "0.75rem",
                md: "0.9rem",
                lg: "1rem",
              },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ display: { xs: "table-cell", sm: "table-cell" } }}
              >
                Order id
              </TableCell>
              <TableCell sx={{ display: { xs: "table-cell", sm: "table-cell" } }}>
                Customer
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recent_orders.length > 0 ? (
              recent_orders.slice(0, 5).map((order, idx) => {
                let chipColor = "default";
                if (order.status === "completed") chipColor = "success";
                else if (order.status === "cancelled") chipColor = "error";
                else if (
                  order.status === "pending" ||
                  order.status === "order_placed"
                )
                  chipColor = "warning";

                return (
                  <TableRow
                    key={order.id || idx}
                    sx={{
                      bgcolor: idx % 2 === 0 ? "grey.50" : "white",
                    }}
                  >
                    <TableCell
                      sx={{ display: { xs: "table-cell", sm: "table-cell" } }}
                    >
                      <Link
                        to={`/edit-order/${order.id}`}
                        style={{
                          textDecoration: "none",
                          color: "#7c0bcc",
                          fontWeight: "bold",
                          fontSize: {
                            xs: "0.65rem",
                            sm: "0.75rem",
                            md: "0.7rem",
                            lg: ".8rem",
                          },
                        }}
                        onMouseOver={(e) =>
                          (e.target.style.textDecoration = "underline")
                        }
                        onMouseOut={(e) =>
                          (e.target.style.textDecoration = "none")
                        }
                      >
                        {order.order_number}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "table-cell", sm: "table-cell" } }}
                    >
                      {order.customer?.name || order.customer_name || "Unknown"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status.replace(/_/g, " ").toUpperCase()}
                        color={chipColor}
                        size="small"
                        sx={{
                          fontSize: {
                            xs: "0.55rem",
                            sm: "0.65rem",
                            md: "0.75rem",
                            lg: "0.8rem",
                          },
                          textTransform: "none",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      ₹{parseFloat(order.total_amount || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{
                    fontSize: {
                      xs: "0.65rem",
                      sm: "0.75rem",
                      md: "0.9rem",
                      lg: "1rem",
                    },
                  }}
                >
                  No recent orders
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  </Grid>
));

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    total_sales: 0,
    total_orders: 0,
    total_products: 0,
    received_orders: 0,
    ready_for_delivery_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
    recent_orders: [],
    monthly_sales: [
      { month: "Jul", total: 0 },
      { month: "Aug", total: 0 },
      { month: "Sep", total: 0 },
    ],
    top_selling_products: [],
  });
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [isBranchesLoading, setIsBranchesLoading] = useState(false);
  const [userBranchId, setUserBranchId] = useState(null);

  const userRole = localStorage.getItem("role");
  const isAdmin = userRole && userRole === "1";

  useEffect(() => {
    const fetchBranches = async () => {
      setIsBranchesLoading(true);
      try {
        const res = await getAllBranchLists();
        const list = res?.data?.data || [];
        const branchList = isAdmin
          ? [
              { id: "all", name: "All Branches" },
              ...list.map((branch) => ({
                id: branch.id,
                name: branch.name,
              })),
            ]
          : list.map((branch) => ({
              id: branch.id,
              name: branch.name,
            }));
        setBranches(branchList);

        if (!isAdmin) {
          const userBranch = localStorage.getItem("branch");
          if (userBranch) {
            setUserBranchId(userBranch);
            setSelectedBranch(userBranch);
          } else {
            toast.error("No branch assigned to this user!");
          }
        } else {
          setSelectedBranch("all");
        }
      } catch (error) {
        toast.error("Failed to fetch branches!");
      } finally {
        setIsBranchesLoading(false);
      }
    };
    fetchBranches();
  }, [isAdmin]);

  const fetchStats = useCallback(async (branchId) => {
    if (!branchId) return;
    setIsStatsLoading(true);
    try {
      let res;
      if (branchId === "all") {
        res = await getDashboardStats({ branchId: null });
      } else {
        res = await getDashboardBranch(branchId);
      }

      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) { // Last 6 months for dynamic display
        const monthIndex = (now.getMonth() - i + 12) % 12;
        months.push({ month: monthNames[monthIndex], total: 0 });
      }

      const monthlySales = Array.isArray(res.data.data.monthly_sales)
        ? res.data.data.monthly_sales
        : [];
      console.log("monthly_sales", monthlySales);

      const updatedMonthlySales = months.map((defaultMonth) => {
        const foundMonth = monthlySales.find((sale) => {
          const saleMonth = sale.month ? String(sale.month).toLowerCase() : "";
          return saleMonth === defaultMonth.month.toLowerCase();
        });
        return {
          month: defaultMonth.month,
          total: foundMonth ? Number(foundMonth.total) || 0 : 0,
        };
      });

      const topSellingProducts = Array.isArray(res.data.data.top_selling_products)
        ? res.data.data.top_selling_products
        : [];
      console.log("top_selling_products raw", topSellingProducts);

      const parsedTopSellingProducts = topSellingProducts.map((product, index) => {
        const totalRevenue = Number(product.total_revenue) || 0;
        console.log(`Product ${index}:`, { name: product.name, total_revenue: totalRevenue });
        return {
          ...product,
          total_revenue: totalRevenue,
          name: product.name || `Unknown Product ${index + 1}`,
        };
      });

      const parsedData = {
        ...res.data.data,
        monthly_sales: updatedMonthlySales,
        top_selling_products: parsedTopSellingProducts,
        recent_orders: res.data.data.recent_orders || [],
      };

      setDashboardData((prev) => ({
        ...prev,
        ...parsedData,
      }));
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      toast.error("Failed to fetch dashboard data!");
    } finally {
      setIsStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedBranch) {
        fetchStats(selectedBranch);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [selectedBranch, fetchStats]);

  const stats = [
    {
      label: "Total Sales Amount",
      value: dashboardData.total_sales,
      icon: (
        <CurrencyRupee
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#2ba182ff",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #425c8dff, #1d3ed1ff)",
    },
    {
      label: "Received Amount",
      value: dashboardData.received_amount,
      icon: (
        <CurrencyRupee
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#0288d1",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #4facfe, #00f2fe)",
    },
    {
      label: "Pending Amount",
      value: dashboardData.pending_amount,
      icon: (
        <CurrencyRupee
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#f57c00",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #fbc02d, #f57c00)",
    },
    {
      label: "Total Orders",
      value: dashboardData.total_orders,
      icon: (
        <ShoppingCartOutlined
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#2ba182ff",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #1DD1A1, #2ba182ff)",
    },
    {
      label: "Received Orders",
      value: dashboardData.received_orders,
      icon: (
        <AssignmentTurnedInOutlined
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#0288d1",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #00c4b4, #0288d1)",
    },
    {
      label: "Ready for Delivery",
      value: dashboardData.ready_for_delivery_orders,
      icon: (
        <LocalShippingOutlined
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#f57c00",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #ff9800, #f57c00)",
    },
    {
      label: "Completed Orders",
      value: dashboardData.completed_orders,
      icon: (
        <CheckCircleOutline
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#388e3c",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #66bb6a, #388e3c)",
    },
    {
      label: "Cancelled Orders",
      value: dashboardData.cancelled_orders,
      icon: (
        <Cancel
          sx={{
            fontSize: { xs: 24, sm: 32, md: 36, lg: 38 },
            color: "#ff1e00ff",
          }}
        />
      ),
      gradient: "linear-gradient(135deg, #ff1e00ff, #8e3838ff)",
    },
  ];

  const displayText =
    isAdmin && selectedBranch === "all"
      ? "Admin"
      : branches.find((branch) => branch.id === selectedBranch)?.name ||
        "Select a branch";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "100%" },
        mb: { xs: 0, sm: 4, md: 4 },
        mt: { xs: 2, sm: 2 },
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3, md: 4 },
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
          width: "100%",
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            color: Color.label.main,
            fontSize: {
              xs: "1.2rem",
              sm: "1.5rem",
              md: "1.8rem",
              lg: "2rem",
              xl: "2.2rem",
            },
          }}
        >
          <span style={{ color: "red" }}>Welcome, </span>
          <span style={{ color: Color.primary.main }}>{displayText}!</span>
        </Typography>
        <Box>
          <FormControl
            sx={{
              width: { xs: "100%", sm: 250, md: 300, lg: 350, xl: 400 },
              maxWidth: "100%",
            }}
            size="small"
          >
            <Select
              value={selectedBranch || ""}
              onChange={(e) => setSelectedBranch(e.target.value)}
              disabled={isBranchesLoading || !isAdmin}
              sx={{
                "& .MuiSelect-select": {
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  py: { xs: 0.5, sm: 1 },
                },
                width: "100%",
              }}
            >
              {isAdmin && !branches.some((branch) => branch.id === "all") && (
                <MenuItem value="all">
                  <Typography
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                    }}
                  >
                    All Branches
                  </Typography>
                </MenuItem>
              )}
              {isBranchesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={{ xs: 16, sm: 18, md: 20 }} />
                </MenuItem>
              ) : (
                branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    <Typography
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                      }}
                    >
                      {branch.name}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid
        container
        spacing={{ xs: 1, sm: 2, md: 3, lg: 3 }}
        sx={{
          width: "100%",
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        {stats.map((stat, i) => (
          <MemoizedCard key={i} stat={stat} />
        ))}
      </Grid>

      <Grid
        container
        spacing={{ xs: 1, sm: 2, md: 3, lg: 3 }}
        mt={{ xs: 2, sm: 3, md: 4 }}
        sx={{
          width: "100%",
          justifyContent: { xs: "center", sm: "space-between" },
        }}
      >
        <MemoizedBarChart monthly_sales={dashboardData.monthly_sales} />
        <MemoizedPieChart
          top_selling_products={dashboardData.top_selling_products}
        />
      </Grid>

      <MemoizedRecentOrders recent_orders={dashboardData.recent_orders} />
    </Box>
  );
};

export default Dashboard;